import { useEffect, useRef, useState } from "react";
import { AppDialog } from "./CustomMdal";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/swiper-bundle.css";
import { Images } from "../../utils/imageConstants";
import {useLocation } from "react-router-dom";

interface ImageZoomModalProps {
  imageSrc?: string;
  images?: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  altText?: string;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
  imageSrc,
  images = [],
  initialIndex = 0,
  isOpen,
  onClose,
  altText = "Zoomed image",
}) => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const [swiperInstance, setSwiperInstance] = useState<any>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const galleryDetailsRoute = location.pathname.includes("/gallery-details")

  // Use images array if provided, otherwise fall back to single imageSrc
  const imageList = images.length > 0 ? images : imageSrc ? [imageSrc] : [];

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Slide to initial index when modal opens or initialIndex changes
  useEffect(() => {
    if (isOpen && swiperInstance && imageList.length > 0) {
      swiperInstance.slideTo(initialIndex, 0);
      setIsBeginning(swiperInstance.isBeginning);
      setIsEnd(swiperInstance.isEnd);
    }
  }, [isOpen, swiperInstance, initialIndex, imageList.length]);

  return (
    <AppDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      className="image-zoom-modal"
      overlayClassName="image-zoom-overlay"
      showCloseIcon={false}
    >
      <div className="image-zoom-content" onClick={onClose}>
        <div className="image-zoom-wrapper">
          <button
            className={galleryDetailsRoute?"image-close-button image-zoom-close":"image-zoom-close"}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close image zoom"
            type="button"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {imageList.length > 1 ? (
            <div 
              className="image-zoom-carousel"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={prevRef}
                className={`image-zoom-nav image-zoom-prev ${isBeginning ? "disabled" : ""}`}
                aria-label="Previous image"
                type="button"
                disabled={isBeginning}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <img src={Images.white_back_arrow} alt="Previous" />
              </button>
              <button
                ref={nextRef}
                className={`image-zoom-nav image-zoom-next ${isEnd ? "disabled" : ""}`}
                aria-label="Next image"
                type="button"
                disabled={isEnd}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <img src={Images.white_back_arrow} alt="Next" />
              </button>

              <Swiper
                modules={[Navigation]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }}
                onInit={(swiper) => {
                  // @ts-ignore – Swiper expects HTMLElement
                  swiper.params.navigation.prevEl = prevRef.current;
                  // @ts-ignore
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.init();
                  swiper.navigation.update();
                  setSwiperInstance(swiper);
                  setIsBeginning(swiper.isBeginning);
                  setIsEnd(swiper.isEnd);
                }}
                onSlideChange={(swiper) => {
                  setIsBeginning(swiper.isBeginning);
                  setIsEnd(swiper.isEnd);
                }}
                initialSlide={initialIndex}
                onClick={(swiper, event) => {
                  event.stopPropagation();
                }}
              >
                {imageList.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={img}
                      alt={`${altText} - ${index + 1}`}
                      className="image-zoom-img"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <img
              src={imageList[0]}
              alt={altText}
              className="image-zoom-img"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      </div>
    </AppDialog>
  );
};

export default ImageZoomModal;

